import { forwardRef, type ReactNode } from 'react';

import { DataTestId, StylingProps } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import {
  CriticalIcon,
  InformationIcon,
  SuccessIcon,
  WarningIcon,
} from '@dynatrace/strato-icons';

import { useMessageContainerContext } from './hooks/useMessageContainerContext.js';

//#region Typings
/**
 * Accepted properties for the MessageContainer Prefix.
 * @public
 */
export interface MessageContainerPrefixProps extends StylingProps, DataTestId {
  /** Elements to be displayed in the prefix slot. */
  children?: ReactNode;
}
//#endregion Typings

const DefaultIcon = () => {
  const { variant } = useMessageContainerContext();

  switch (variant) {
    case 'primary':
      return <InformationIcon />;
    case 'success':
      return <SuccessIcon />;
    case 'warning':
      return <WarningIcon />;
    case 'critical':
      return <CriticalIcon />;
    case 'neutral':
    default:
      return <InformationIcon />;
  }
};

/**
 * The MessageContainer Prefix component.
 * @public
 */
export const Prefix = forwardRef<HTMLDivElement, MessageContainerPrefixProps>(
  (props, forwardedRef) => {
    const {
      children,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: MessageContainerPrefixProps = props;

    return (
      <Flex
        data-testid={dataTestId}
        ref={forwardedRef}
        className={consumerClassName}
        style={consumerStyle}
        {...remainingProps}
      >
        {children || <DefaultIcon />}
      </Flex>
    );
  },
);

(Prefix as typeof Prefix & { displayName: string }).displayName =
  'MessageContainer.Prefix';
